/**
 * Thin fetch wrapper that sends credentials (cookies) and handles the
 * "401 → bounce to login" case in one place. Every page uses this.
 */

export async function api<T = unknown>(
  path: string,
  init: RequestInit = {}
): Promise<T> {
  const res = await fetch(path, {
    credentials: 'include',
    ...init,
    headers: {
      'Content-Type': 'application/json',
      ...(init.headers || {}),
    },
  });

  if (res.status === 401) {
    // Distinguish "no session" from "this session can't do that".
    //
    // The worker answers a missing/expired session with
    // { error: 'unauthorized', reason: 'no_session' }. Only that case should
    // bounce to /login. A 401 that carries any other reason — or a 403 — means
    // the user IS signed in but lacks permission for this particular call
    // (e.g. a read-only `demo` role hitting a settings endpoint).
    //
    // Redirecting on those looked exactly like a random sign-out: the session
    // was fine, one widget's request was refused, and the whole app navigated
    // to the login page. Let the caller handle it instead.
    let noSession = false;
    try {
      const body = (await res.clone().json()) as { reason?: string };
      noSession = body?.reason === 'no_session';
    } catch {
      // Non-JSON 401 — treat as a genuine auth failure.
      noSession = true;
    }

    if (noSession && typeof window !== 'undefined' && window.location.pathname !== '/login') {
      const next = encodeURIComponent(window.location.pathname + window.location.search);
      window.location.href = `/login?next=${next}`;
      throw new Error('Unauthorized');
    }
    throw new Error(noSession ? 'Unauthorized' : 'Forbidden');
  }

  if (res.status === 403) {
    // Mandatory-MFA gate: the session is valid but must step up with a
    // passkey (org has servers). Bounce to the verification page, which
    // sends the user back here afterwards.
    let mfaRequired = false;
    try {
      const body = (await res.clone().json()) as { error?: string; reason?: string };
      mfaRequired = body?.error === 'mfa_required' || body?.reason === 'mfa_required';
    } catch {
      /* non-JSON 403 — fall through to the generic error */
    }
    if (
      mfaRequired &&
      typeof window !== 'undefined' &&
      window.location.pathname !== '/mfa-verify'
    ) {
      const next = encodeURIComponent(window.location.pathname + window.location.search);
      window.location.href = `/mfa-verify?next=${next}`;
      throw new Error('MFA required');
    }
  }

  if (!res.ok) {
    const body = await res.text().catch(() => '');
    throw new Error(`HTTP ${res.status}: ${body || res.statusText}`);
  }

  return res.json() as Promise<T>;
}
